/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import { SkipLink } from 'sarsaparilla';

/**
 * Renders a skip link in the top nav to skip to the chatbot
 */
export function ChatSkipLink() {
    const [dfMessenger, setDfMessenger] = React.useState<Element | null>(null);
    const id = 'widgetIcon';

    // On mount, start looking for the chat widget. It loads async, so we do an exponential
    // back-off style polling method. Once we find it, we set it in state for use further down.
    React.useEffect(() => {
        getChatElement().then((element) => {
            setDfMessenger(element);
        });
    }, []);

    const handleClick = () => {
        if (!dfMessenger) {
            return;
        }

        if (dfMessenger) {
            const root = dfMessenger.shadowRoot;

            if (root && root.children.length) {
                const nodes = root.children;
                const widget: HTMLElement | null = nodes[1].querySelector(`#${id}`);

                if (widget) {
                    widget.focus();
                }
            }
        }
    };

    // return null if the chat widget is not found
    if (!dfMessenger) {
        return null;
    }

    return (
        <SkipLink
            href={`#${id}`}
            onClick={handleClick}
            position="containerTop"
            zIndex="high"
        >
            Skip to Help Chatbot
        </SkipLink>
    );
}

function delay(retryCount: number) {
    return new Promise((resolve) => {
        setTimeout(resolve, 2 ** retryCount * 1000);
    });
}

async function getChatElement(retryCount = 0): Promise<Element | null> {
    if (retryCount > 4) return Promise.resolve(null);

    const el = document.querySelector('df-messenger');

    // If we found the element, return it
    if (el) {
        return el;
    }

    // Otherwise we're wait and then try again
    await delay(retryCount);
    return getChatElement(retryCount + 1);
}
